import { verifiedProperties, type PropertyCommunity, type VerifiedProperty } from '@/mocks/properties';
import * as beachBeachBabyData from '@/pages/property-detail/data/beach-beach-baby';
import * as bellaVitaData from '@/pages/property-detail/data/bella-vita';
import * as belvedereData from '@/pages/property-detail/data/belvedere';
import * as hannahsPearlData from '@/pages/property-detail/data/hannahs-pearl';
import * as seaDreamsData from '@/pages/property-detail/data/sea-dreams';
import * as sunsetBungalowData from '@/pages/property-detail/data/sunset-bungalow-30a';

export type ConciergeExchange = {
  answer: string;
  refs?: string[];
  action?: 'contact';
};

type AmenityKey =
  | 'beachfront'
  | 'privatePool'
  | 'elevator'
  | 'rooftop'
  | 'gulfViews'
  | 'carriageHouse'
  | 'outdoorLiving'
  | 'summerKitchen'
  | 'bikes'
  | 'golfCart';

type AmenityDef = {
  key: AmenityKey;
  phrase: string;
  verb: 'has' | 'is';
  patterns: RegExp[];
};

type PropertyDetailContent = {
  storyData: {
    paragraphs: string[];
    locationContext: string;
    address: string;
    addressSub: string;
    propertyMeta: string;
  };
  signatureFeatures: { title: string; body: string; tag: string }[];
  essentialInfoGroups: { heading: string; items: string[] }[];
};

type PlaceKnowledge = {
  aliases: string[];
  summary: string;
  goodFor: string[];
};

type DemoPropertyKnowledge = {
  propertyFacts: string[];
  roomLayoutFacts: string[];
  amenityFacts: string[];
  locationFacts: string[];
  nearbyPlaces: string[];
  knownUnknowns: string[];
};

const AMENITIES: AmenityDef[] = [
  { key: 'golfCart', phrase: 'a golf cart', verb: 'has', patterns: [/golf\s*[- ]?cart/i] },
  { key: 'bikes', phrase: 'bikes', verb: 'has', patterns: [/\bbikes?\b/i] },
  { key: 'privatePool', phrase: 'a private pool', verb: 'has', patterns: [/\bpool\b/i] },
  { key: 'beachfront', phrase: 'beachfront', verb: 'is', patterns: [/beach\s*front/i, /beachfront/i, /on\s+the\s+sand/i, /feet[-\s]*in[-\s]*the[-\s]*sand/i] },
  { key: 'elevator', phrase: 'a private elevator', verb: 'has', patterns: [/elevator/i] },
  { key: 'rooftop', phrase: 'a rooftop living area with Gulf views', verb: 'has', patterns: [/roof\s*top/i, /rooftop/i] },
  { key: 'carriageHouse', phrase: 'a carriage house', verb: 'has', patterns: [/carriage/i] },
  { key: 'summerKitchen', phrase: 'a summer kitchen', verb: 'has', patterns: [/summer\s+kitchen/i] },
  { key: 'gulfViews', phrase: 'Gulf views', verb: 'has', patterns: [/gulf\s+views?/i, /beach\s+views?/i, /water\s+views?/i, /\bviews?\b/i] },
  { key: 'outdoorLiving', phrase: 'an outdoor living space', verb: 'has', patterns: [/outdoor\s+living/i] },
];

const PROPERTY_DETAIL_CONTENT: Record<string, PropertyDetailContent> = {
  belvedere: belvedereData,
  'hannahs-pearl': hannahsPearlData,
  'sea-dreams': seaDreamsData,
  'beach-beach-baby': beachBeachBabyData,
  'bella-vita': bellaVitaData,
  'sunset-bungalow-30a': sunsetBungalowData,
};

const PLACE_GLOSSARY: Record<string, PlaceKnowledge> = {
  'the-big-chill-30a': {
    aliases: ['the hub', 'hub 30a', 'the hub 30a', 'big chill', 'the big chill', 'big chill 30a', 'the big chill 30a'],
    summary:
      'The Big Chill 30A, formerly The Hub 30A, is an indoor/outdoor 30A gathering spot with multiple restaurants, bars, shops, live entertainment, and a central lawn.',
    goodFor: ['restaurants', 'bars', 'live music', 'casual group dining', 'families', 'shopping'],
  },
  'rosemary-beach': {
    aliases: ['rosemary', 'rosemary beach'],
    summary:
      'Rosemary Beach is a walkable 30A beach town with restaurants, rooftop dining, cafes, boutiques, shops, and beach access close together.',
    goodFor: ['restaurants', 'bars', 'boutiques', 'walkability', 'beach access', 'date-night dining'],
  },
  'pescado': {
    aliases: ['pescado'],
    summary: 'Pescado is a Rosemary Beach seafood restaurant and rooftop bar overlooking the Gulf.',
    goodFor: ['seafood', 'rooftop bar', 'Gulf views', 'dinner'],
  },
  'restaurant-paradis': {
    aliases: ['restaurant paradis', 'paradis'],
    summary: 'Restaurant Paradis is an intimate Rosemary Beach fine-dining restaurant focused on Gulf seafood, steaks, wine, and cocktails.',
    goodFor: ['fine dining', 'cocktails', 'date night', 'special occasions'],
  },
  'la-crema': {
    aliases: ['la crema'],
    summary: 'La Crema is a Rosemary Beach tapas and chocolate restaurant close to the town center.',
    goodFor: ['tapas', 'dessert', 'wine', 'walkable dining'],
  },
  edwards: {
    aliases: ['edwards', "edward's"],
    summary: 'Edwards is a Rosemary Beach restaurant close to the town center.',
    goodFor: ['walkable dining', 'dinner'],
  },
  seaside: {
    aliases: ['seaside'],
    summary: 'Seaside is a classic 30A town known for walkable restaurants, shops, food trucks, beach access, and a lively town-center feel.',
    goodFor: ['restaurants', 'shopping', 'food trucks', 'families', 'walkability'],
  },
  'pier-park': {
    aliases: ['pier park'],
    summary:
      'Pier Park is Panama City Beach’s large shopping, dining, and entertainment district, with restaurants, shops, attractions, and a movie theater.',
    goodFor: ['restaurants', 'shopping', 'entertainment', 'movies', 'family activities'],
  },
  'stinkys-fish-camp': {
    aliases: ["stinky's", 'stinkys', "stinky's fish camp", 'stinkys fish camp'],
    summary: "Stinky's Fish Camp is a Dune Allen restaurant and bar across from Sunset Bungalow on 30A.",
    goodFor: ['seafood', 'bar', 'walkable dining', 'Dune Allen'],
  },
  'shunk-gulley': {
    aliases: ['shunk gulley'],
    summary: 'Shunk Gulley is a 30A restaurant and bar near Dune Allen.',
    goodFor: ['restaurant', 'bar', 'live music', '30A dining'],
  },
  'perfect-pig': {
    aliases: ['perfect pig', 'the perfect pig'],
    summary: 'The Perfect Pig is a 30A restaurant near Dune Allen.',
    goodFor: ['breakfast', 'lunch', 'dinner', '30A dining'],
  },
  'vue-on-30a': {
    aliases: ['vue on 30a', 'vue'],
    summary: 'Vue on 30A is a Gulf-view 30A restaurant near Dune Allen.',
    goodFor: ['Gulf-view dining', 'restaurant', '30A dining'],
  },
  'emerald-beach-resort': {
    aliases: ['emerald beach', 'emerald beach resort'],
    summary:
      'Emerald Beach Resort is a Gulf-front Panama City Beach resort with pools, hot tub, splash pad, Sky Bar, Starbucks, beach access, and covered parking.',
    goodFor: ['resort pools', 'beach access', 'Sky Bar', 'Starbucks', 'families'],
  },
  'watersound-west': {
    aliases: ['watersound west', 'water sound west', 'watersound'],
    summary:
      'WaterSound West is a private gated 30A community with a clubhouse, walk-in community pool, nature trails, and a beach boardwalk to private beach access.',
    goodFor: ['quiet 30A setting', 'community pool', 'private beach access', 'bike rides'],
  },
  'alys-beach': {
    aliases: ['alys', 'alys beach'],
    summary: 'Alys Beach is a walkable 30A town with dining and shopping near Rosemary Beach.',
    goodFor: ['dining', 'shopping', 'walkability'],
  },
};

const PROPERTY_KNOWLEDGE: Record<string, DemoPropertyKnowledge> = {
  belvedere: {
    propertyFacts: ['7 bedrooms', '9 bathrooms', 'sleeps up to 20 guests', '5 levels', '7,400 sq. ft.', 'completed in late 2020'],
    roomLayoutFacts: [
      'Primary suite has a king bed, two private bathrooms, sitting room, and balcony overlooking Rosemary Beach Town Center',
      '4 additional king bedroom suites each have private bathroom and walk-in closet',
      '2 custom bunk rooms',
      'Two gathering rooms on Level 2 and a third gathering room on Level 4',
    ],
    amenityFacts: [
      'Private pool with programmable lights, fountains, and jets',
      'Private elevator serving all five levels',
      '1,000+ sq. ft. rooftop outdoor living area with 180-degree Gulf views',
      'Natural gas grill and outdoor living terrace',
      'Private parking for two vehicles including a private garage',
      'Beach towels provided',
      'Pet friendly with additional fee and pre-approval',
    ],
    locationFacts: [
      'Located in the heart of Rosemary Beach',
      'Steps to Restaurant Paradis, Pescado, La Crema, and Edwards',
      'Walk or bike to Alys Beach and WaterColor',
      'Publix Grocery, Frank Brown Park, Pier Park, and Shipwreck Island Water Park are listed nearby',
    ],
    nearbyPlaces: ['rosemary-beach', 'restaurant-paradis', 'pescado', 'la-crema', 'edwards', 'alys-beach', 'pier-park'],
    knownUnknowns: ['Exact walk time to each restaurant', 'Current pet fees', 'Current pool heat fee', 'Current availability and pricing'],
  },
  'hannahs-pearl': {
    propertyFacts: ['5 bedrooms', '5.5 bathrooms', 'sleeps up to 12 guests', '2,948 sq. ft.', 'totally renovated', 'WaterSound West gated community'],
    roomLayoutFacts: [
      'Primary suite has king bed, soaking tub, glass-front walk-in shower, and dual-sink granite vanity',
      'Main-level guest bedroom has queen bed and en suite shower/tub',
      'Second level has two queen bedrooms, each with en suite bath',
      'Media room has 65-inch TV, seating, and queen sleeper sofa',
      'Carriage house has private entrance, kitchen, king bed, and walk-in shower',
    ],
    amenityFacts: [
      'Oversized custom private pool with sun shelf, heated for additional fee',
      'Summer kitchen with gas grill, Traeger wood-fired smoker, and Big Green Egg',
      'Gas fire pit',
      'Two screened porches',
      'Golf cart included with every rental',
      '4 bicycles included',
      'Sonos sound system throughout',
      'WaterSound West community clubhouse, pool, trails, and beach boardwalk',
    ],
    locationFacts: [
      '1.5 miles to The Hub 30A, now known as The Big Chill 30A',
      'Old Florida Fish House and Buddy’s Seafood Market are just up the road',
      '3.5 miles to Seaside',
      '5 miles to Rosemary Beach',
      'Deer Lake State Park and Horseshoe Lake are nearby',
    ],
    nearbyPlaces: ['watersound-west', 'the-big-chill-30a', 'seaside', 'rosemary-beach'],
    knownUnknowns: ['Pet policy is not listed on the demo property facts', 'Exact parking count is not listed', 'Current pool heat fee', 'Current availability and pricing'],
  },
  'sea-dreams': {
    propertyFacts: ['5 bedrooms', '5 bathrooms', 'sleeps up to 16 guests', '3 levels', 'newly renovated', 'beachfront on Thomas Drive'],
    roomLayoutFacts: [
      'Every bedroom has its own television and bathroom',
      'Living room has queen sleeper sofa',
      'Primary suite has king bed, jetted tub, walk-in shower, and Gulf balcony',
      'Third-floor bedroom has queen bed, two twin beds, mini refrigerator, wet bar, and private Gulf balcony',
    ],
    amenityFacts: [
      'Gulf-front private pool heated for additional fee',
      'Direct beach access steps from the pool',
      'Gulf views from all three levels',
      'Gas grill outside',
      'Extra refrigerator in garage',
      'Parking for up to 3 vehicles depending on size and type',
      'Pack-and-play, high chair, Xbox, board games, washer, dryer, and complimentary Wi-Fi',
    ],
    locationFacts: [
      'Located on Thomas Drive in Panama City Beach',
      'Restaurants, bars, and shopping are within walking distance',
      'Pier Park is 5 miles away with restaurants, shops, entertainment, and the Grand 16 IMAX Theater',
      'Publix Sports Complex and Frank Brown Park are nearby',
    ],
    nearbyPlaces: ['pier-park'],
    knownUnknowns: ['Exact names of walkable Thomas Drive bars/restaurants are not listed', 'Pet policy is not listed', 'Current pool heat fee', 'Current availability and pricing'],
  },
  'beach-beach-baby': {
    propertyFacts: ['9 bedrooms', '11 bathrooms', 'sleeps up to 28 guests', 'more than 5,200 sq. ft.', '3 levels', 'built in 2023'],
    roomLayoutFacts: [
      'Level 1 has two king bedrooms with two twin bunk beds each',
      'Level 1 has a bedroom with two queen-over-queen bunk beds',
      'Level 2 has two king bedrooms',
      'Level 3 has two king bedrooms plus two Gulf-front primary suites with king beds',
      'Two great rooms and a large open-concept layout',
    ],
    amenityFacts: [
      'Private pool and hot tub, heated for additional fee',
      'Elevator serving all three levels',
      'Beach access directly out the front door',
      'Massive Level 2 and Level 3 balconies with Gulf views',
      'Outdoor living area with lounge chairs, dining table, rocking chairs, gas grill, and outdoor shower',
      'Parking for 6 to 8 vehicles depending on size and type',
      'Pet friendly with additional fee',
    ],
    locationFacts: [
      'Located on the quiet West End of Panama City Beach',
      'Minutes from scenic Highway 30A, Rosemary Beach, and Alys Beach',
      'Close drive to Pier Park',
      'Close to Publix Sports Complex and Frank Brown Park',
    ],
    nearbyPlaces: ['pier-park', 'rosemary-beach', 'alys-beach'],
    knownUnknowns: ['Exact walkability to restaurants is not listed', 'Current pet fees', 'Current pool heat fee', 'Current availability and pricing'],
  },
  'bella-vita': {
    propertyFacts: ['2 bedrooms', '2 bathrooms', 'sleeps up to 6 guests', '1,299 sq. ft.', '25th-floor Gulf-front penthouse', 'Emerald Beach Resort'],
    roomLayoutFacts: [
      'Primary bedroom is Gulf-front with king bed and balcony access',
      'Primary bathroom has double sinks, garden tub, and separate walk-in shower',
      'Guest bedroom has queen bed and private access to shared bathroom',
      'Bunk area has twin bunk beds',
    ],
    amenityFacts: [
      'Massive balcony overlooking the Gulf of Mexico',
      '180-degree beach and Gulf views',
      'Several resort pools, hot tub, splash pad, whirlpool spa grotto, tropical pool deck',
      'On-site Starbucks, poolside cafe, Sky Bar',
      'Covered parking garage and connected sky bridge',
      'Two beach chairs and umbrella setup included March 1 through October 31',
    ],
    locationFacts: [
      'Located in the heart of Panama City Beach',
      'Pier Park is just down the street',
      'More than 130 shops, restaurants, and entertainment venues nearby',
      'Grand 16 IMAX Theater nearby',
      'Shipwreck Island Water Park less than 2 miles away',
    ],
    nearbyPlaces: ['emerald-beach-resort', 'pier-park'],
    knownUnknowns: ['Exact parking count is not listed', 'Pet policy is not listed', 'Current resort amenity rules', 'Current availability and pricing'],
  },
  'sunset-bungalow-30a': {
    propertyFacts: ['4 bedrooms', '2 bathrooms', 'sleeps up to 10 guests', 'Dune Allen on 30A', 'beach access steps away', 'pet friendly with additional fee'],
    roomLayoutFacts: [
      'Primary suite has king bed, 55-inch TV, outdoor access, Gulf views, walk-in shower, and double vanity',
      'Bedroom 2 has queen bed and 40-inch TV',
      'Bedroom 3 has queen bed and 40-inch TV',
      'Bedroom 4 has twin bunk bed and 32-inch TV',
      'Guest bedrooms share second full bathroom with shower/tub combination',
    ],
    amenityFacts: [
      'Expansive outdoor living space',
      'Front porch overlooking Allen Lake',
      'Gas grill',
      '4 bikes included',
      'Wi-Fi, washer and dryer, cable/smart television',
      'Parking available on-site for up to 4 vehicles',
    ],
    locationFacts: [
      'Located in Dune Allen on 30A',
      'Across the street from Stinky’s Fish Camp',
      '1.5 miles from Shunk Gulley',
      '1.5 miles from Perfect Pig',
      '1.5 miles from Vue on 30A',
    ],
    nearbyPlaces: ['stinkys-fish-camp', 'shunk-gulley', 'perfect-pig', 'vue-on-30a'],
    knownUnknowns: ['Current pet fees', 'Current bike condition/availability beyond listed inclusion', 'Current availability and pricing'],
  },
};

const DETAIL_TOPICS = [
  {
    label: 'kitchen',
    patterns: /\bkitchen|cook|cooking|chef|appliances?|dining|coffee|refrigerator|fridge|dishwasher|cookware|utensils/i,
  },
  {
    label: 'bedrooms',
    patterns: /\bbedrooms?|beds?|sleep(?:ing)?|primary suite|bunk|king|queen|twin|sofa sleeper|layout/i,
  },
  {
    label: 'bathrooms',
    patterns: /\bbath(?:room)?s?|shower|tub|jetted|vanity/i,
  },
  {
    label: 'pool',
    patterns: /\bpool|hot tub|spa|heated|sun shelf|fountains?|jets/i,
  },
  {
    label: 'beach access',
    patterns: /\bbeach|beachfront|sand|shore|access|chairs?|umbrella/i,
  },
  {
    label: 'views',
    patterns: /\bviews?|gulf|balcon(?:y|ies)|rooftop|porch|deck|outdoor|outside|terrace|lake/i,
  },
  {
    label: 'location',
    patterns: /\blocation|area|near|walk|drive|where|rosemary|watersound|dune allen|thomas drive|pier park|stinky|30a|panama/i,
  },
  {
    label: 'resort amenities',
    patterns: /\bamenit(?:y|ies)|resort|fitness|starbucks|sky bar|splash pad|garage|parking|bridge/i,
  },
  {
    label: 'golf cart and bikes',
    patterns: /\bgolf\s*[- ]?cart|bikes?|bicycles?|ride/i,
  },
  {
    label: 'elevator',
    patterns: /\belevator|stairs?|levels?|floor/i,
  },
  {
    label: 'pets',
    patterns: /\bpet|pets|dog|dogs|cat|cats|animals?/i,
  },
] as const;

function joinNatural(list: string[]): string {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
}

function sentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function sentenceList(items: string[]): string {
  return items.map(sentence).join(' ');
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

function amenityPhrases(p: VerifiedProperty): string[] {
  return p.verifiedAmenityPhrases;
}

const VERIFIED_DETAIL_PATTERNS: RegExp[] = [
  /hot\s*tub/i,
  /beach\s*access|steps?\s+to\s+the\s+beach/i,
  /parking|garage/i,
  /starbucks|sky\s*bar|resort/i,
  /fitness/i,
  /beach\s+chairs?|umbrella/i,
  /sonos|sound\s+system/i,
  /high\s*chair|pack\s+and\s+play|x-?box|board\s+games?/i,
  /grill|smoker|big\s+green\s+egg|summer\s+kitchen/i,
  /outdoor\s+shower/i,
  /balcon(?:y|ies)|porch|rooftop/i,
  /pet|pets|dog|dogs|cat|cats|animals?/i,
];

function matchingDetailPhrases(p: VerifiedProperty, text: string): string[] {
  if (!asksForVerifiedDetail(text)) return [];
  const words = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length >= 4);

  return p.verifiedAmenityPhrases.filter((phrase) => {
    const lower = phrase.toLowerCase();
    return words.some((word) => lower.includes(word));
  });
}

function asksForVerifiedDetail(text: string): boolean {
  return VERIFIED_DETAIL_PATTERNS.some((re) => re.test(text));
}

function extractGuestCount(text: string): number | null {
  const m = text.match(/(\d{1,3})\s*(?:guests?|people|persons?|of\s+us)/i);
  if (m) return parseInt(m[1], 10);
  const m2 = text.match(/room\s+for\s+(\d{1,3})/i);
  if (m2) return parseInt(m2[1], 10);
  return null;
}

function detectRegion(
  text: string,
): PropertyCommunity | null {
  if (/panama\s*[- ]?city|thomas\s+drive/i.test(text)) return 'Panama City Beach';
  if (/water\s*sound|watersound/i.test(text)) return 'WaterSound';
  if (/dune\s+allen/i.test(text)) return 'Dune Allen';
  if (/santa\s+rosa/i.test(text)) return 'Santa Rosa Beach';
  if (/rosemary/i.test(text)) return 'Rosemary Beach';
  if (/30\s*a|thirty\s*a|\b30a\b/i.test(text)) return '30A';
  return null;
}

function inRegion(p: VerifiedProperty, region: PropertyCommunity): boolean {
  return p.communities.includes(region);
}

function detectAmenities(text: string): AmenityKey[] {
  const found: AmenityKey[] = [];
  for (const a of AMENITIES) {
    if (a.patterns.some((re) => re.test(text))) found.push(a.key);
  }
  return found;
}

function detectNamedProperties(text: string): VerifiedProperty[] {
  const t = text.toLowerCase();
  return verifiedProperties.filter((p) => {
    const n = p.name.toLowerCase();
    if (t.includes(n)) return true;
    if (p.id === 'belvedere' && /\bbelv(?:e|a)?dere\b|\bbelvedere\b/i.test(text)) return true;
    if (p.id === 'hannahs-pearl' && /\bhana+h?'?s?\b|\bpe+a?r?l\b|\bp[eu]+r?l\b|\bpeal\b/i.test(text)) return true;
    if (p.id === 'sea-dreams' && /\bsea\s+dreams?\b/i.test(text)) return true;
    if (p.id === 'beach-beach-baby' && /\bbeach\s+beach\s+baby\b/i.test(text)) return true;
    if (p.id === 'bella-vita' && /\bbella\s+vita\b/i.test(text)) return true;
    if (p.id === 'sunset-bungalow-30a' && /\bsunset\s+bungalow\b/i.test(text)) return true;
    const lastWord = n.split(' ').pop();
    return !!lastWord && lastWord.length > 4 && t.includes(lastWord);
  });
}

function referencesSavedSet(text: string): boolean {
  return /\bsaved\b|\bfavorit|\bfavou|\bthese\b|\bmy (?:homes?|properties)\b|\bselected\b/i.test(text);
}

function questionTopic(text: string): (typeof DETAIL_TOPICS)[number] | null {
  return DETAIL_TOPICS.find((topic) => topic.patterns.test(text)) ?? null;
}

function normalizeForMatch(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length >= 4 && !['what', 'does', 'like', 'about', 'tell', 'this', 'that', 'there', 'with', 'have'].includes(word));
}

function shortenDetail(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length <= 420) return trimmed;
  const sentences = trimmed.match(/[^.!?]+[.!?]+/g);
  if (!sentences) return `${trimmed.slice(0, 390).trim()}...`;
  const selected: string[] = [];
  for (const sentence of sentences) {
    if ((selected.join(' ') + sentence).length > 420) break;
    selected.push(sentence.trim());
  }
  return selected.length ? selected.join(' ') : `${trimmed.slice(0, 390).trim()}...`;
}

function formatGroupAnswer(propertyName: string, heading: string, items: string[]): string {
  const cleanHeading = heading.toLowerCase();
  const text = items.map((item, index) => (index === 0 ? item : lowerFirst(item))).join('; ');

  if (/kitchen/.test(cleanHeading)) {
    return `${propertyName}: The kitchen has ${text}.`;
  }
  if (/bedrooms?|suites?|sleeping/.test(cleanHeading)) {
    return `${propertyName}: The sleeping layout includes ${text}.`;
  }
  if (/bath/.test(cleanHeading)) {
    return `${propertyName}: The bathroom details listed are ${text}.`;
  }
  if (/area|neighborhood|community|around/.test(cleanHeading)) {
    return `${propertyName}: Nearby, you’ll find ${text}.`;
  }
  if (/features?|amenities|outdoor|resort/.test(cleanHeading)) {
    return `${propertyName} includes ${text}.`;
  }
  if (/glance/.test(cleanHeading)) {
    return `${propertyName} at a glance: ${text}.`;
  }

  return `${propertyName}: ${text}.`;
}

function scoreText(text: string, topic: (typeof DETAIL_TOPICS)[number] | null, words: string[]): number {
  const lower = text.toLowerCase();
  let score = topic?.patterns.test(lower) ? 8 : 0;
  for (const word of words) {
    if (lower.includes(word)) score += 2;
  }
  return score;
}

function propertyDetailAnswer(p: VerifiedProperty, text: string): string | null {
  const detail = PROPERTY_DETAIL_CONTENT[p.id];
  const topic = questionTopic(text);
  const words = normalizeForMatch(text);
  if (!detail || (!topic && words.length === 0)) return null;
  const candidates: { answer: string; score: number }[] = [];

  for (const feature of detail.signatureFeatures) {
    const combined = `${feature.title} ${feature.tag} ${feature.body}`;
    const score = scoreText(combined, topic, words);
    if (score > 0) candidates.push({ answer: `${feature.title}: ${shortenDetail(feature.body)}`, score: score + 3 });
  }

  for (const group of detail.essentialInfoGroups) {
    const matchingItems = group.items.filter((item) => scoreText(`${group.heading} ${item}`, topic, words) > 0);
    const groupScore = scoreText(`${group.heading} ${group.items.join(' ')}`, topic, words);
    if (matchingItems.length > 0 || groupScore > 0) {
      const items = (matchingItems.length ? matchingItems : group.items).slice(0, 5);
      candidates.push({
        answer: formatGroupAnswer(p.name, group.heading, items),
        score: groupScore + matchingItems.length * 3 + 2,
      });
    }
  }

  for (const paragraph of detail.storyData.paragraphs) {
    const score = scoreText(paragraph, topic, words);
    if (score > 0) candidates.push({ answer: shortenDetail(paragraph), score });
  }

  if (topic?.label === 'location') {
    candidates.push({
      answer: `${p.name} is in ${detail.storyData.locationContext}. ${detail.storyData.address}${detail.storyData.addressSub ? `, ${detail.storyData.addressSub}` : ''}.`,
      score: 10,
    });
  }

  const best = candidates.sort((a, b) => b.score - a.score)[0];
  if (!best) return null;

  return best.answer.startsWith(p.name) ? best.answer : `${p.name}: ${best.answer}`;
}

function handoffAnswer(p: VerifiedProperty, text: string): ConciergeExchange {
  const variants = [
    `That’s a good question, but I don’t see that specific detail listed for ${p.name}. The Inspire team can confirm it before you book.`,
    `I don’t want to guess on that one. I don’t see that detail in the ${p.name} information I have here.`,
    `I don’t see that specific detail listed for ${p.name}. That would be a good one for the Inspire team to confirm directly.`,
  ];
  const answer = variants[text.length % variants.length];
  return { answer, refs: [p.name] };
}

function amenityPivotAnswer(p: VerifiedProperty, lacks: string[]): string {
  if (lacks.includes('Gulf views')) {
    if (p.beachfront) {
      return `${p.name} is beachfront, so even if the exact view question depends on the room or level, the Gulf is the main setting of the stay.`;
    }
    if (/rosemary/i.test(p.location)) {
      return `${p.name} is not directly beachfront, but it does have rooftop outdoor living with Gulf views and a central Rosemary Beach location close to the beach, restaurants, and shops.`;
    }
    if (/dune allen/i.test(p.location)) {
      return `${p.name} is not directly beachfront, but it does offer Gulf views and beach access just steps away from the backyard.`;
    }
    if (/watersound/i.test(p.location)) {
      return `${p.name} is not directly Gulf-front, but it is in WaterSound West with private beach access, a golf cart, bikes, a private pool, and a summer kitchen.`;
    }
  }

  const phrases = amenityPhrases(p);
  return `${p.name} may not be the strongest match for ${joinNatural(lacks)}.${phrases.length ? ` What it does offer: ${joinNatural(phrases)}.` : ''}`;
}

function detectPlaces(text: string): string[] {
  const lower = text.toLowerCase();
  return Object.entries(PLACE_GLOSSARY)
    .filter(([, place]) => place.aliases.some((alias) => lower.includes(alias)))
    .map(([key]) => key);
}

function glossarySummary(keys: string[], intent: RegExp): string[] {
  return keys
    .map((key) => PLACE_GLOSSARY[key])
    .filter((place): place is PlaceKnowledge => Boolean(place))
    .filter((place) => place.goodFor.some((item) => intent.test(item)) || intent.test(place.summary))
    .map((place) => place.summary);
}

function propertyKnowledgeAnswer(p: VerifiedProperty, text: string): ConciergeExchange | null {
  const knowledge = PROPERTY_KNOWLEDGE[p.id];
  if (!knowledge) return null;

  if (/\bdog\s+run|fenced\s+(?:yard|area)|dog\s+area\b/i.test(text)) {
    return handoffAnswer(p, text);
  }

  const placeMatches = detectPlaces(text);
  if (placeMatches.length > 0 && /\bwhat(?:'s| is)|tell me about|explain|where is|who is\b/i.test(text)) {
    return {
      answer: placeMatches.map((key) => PLACE_GLOSSARY[key]?.summary).filter(Boolean).join(' '),
      refs: [p.name],
    };
  }

  if (/\bhow many (?:people|guests)|how many does .*sleep|sleep\??$|sleeps?\b/i.test(text) && !extractGuestCount(text)) {
    return {
      answer: `${p.name} sleeps up to ${p.sleeps} guests, with ${p.bedrooms} bedrooms and ${p.bathrooms} bathrooms.`,
      refs: [p.name],
    };
  }

  if (/\bgolf\s*[- ]?cart\b/i.test(text)) {
    if (p.golfCart) {
      const fact = knowledge.amenityFacts.find((item) => /golf\s*[- ]?cart/i.test(item));
      return {
        answer: fact ? `Yes — ${p.name} has a golf cart included with every rental.` : `Yes — ${p.name} has a golf cart included.`,
        refs: [p.name],
      };
    }
    return {
      answer: `${p.name} does not list a golf cart in the property details. ${amenityPivotAnswer(p, ['a golf cart'])}`,
      refs: [p.name],
    };
  }

  if (/\bbikes?|bicycles?\b/i.test(text)) {
    if (p.bikes) {
      const fact = knowledge.amenityFacts.find((item) => /\bbikes?|bicycles?\b/i.test(item));
      return {
        answer: `Yes — ${p.name} has ${fact ? lowerFirst(fact) : 'bikes included'}.`,
        refs: [p.name],
      };
    }
    return {
      answer: `${p.name} does not list bikes in the property details. ${amenityPivotAnswer(p, ['bikes'])}`,
      refs: [p.name],
    };
  }

  if (/\bhot\s*tub|spa\b/i.test(text)) {
    const fact = knowledge.amenityFacts.find((item) => /\bhot\s*tub|spa\b/i.test(item));
    return fact
      ? { answer: `Yes — ${p.name} has ${lowerFirst(fact)}.`, refs: [p.name] }
      : handoffAnswer(p, text);
  }

  if (/\bgrill|smoker|big\s+green\s+egg|outdoor kitchen|summer kitchen\b/i.test(text)) {
    const fact = knowledge.amenityFacts.find((item) => /\bgrill|smoker|big\s+green\s+egg|summer kitchen\b/i.test(item));
    return fact
      ? { answer: `Yes — ${p.name} has ${lowerFirst(fact)}.`, refs: [p.name] }
      : handoffAnswer(p, text);
  }

  if (/\bcarriage\s+house\b/i.test(text)) {
    const fact = [...knowledge.roomLayoutFacts, ...knowledge.amenityFacts].find((item) => /\bcarriage\s+house\b/i.test(item));
    return fact
      ? { answer: `${p.name}: ${sentence(fact)}`, refs: [p.name] }
      : handoffAnswer(p, text);
  }

  if (/\bparking|cars?|vehicles?|garage\b/i.test(text)) {
    const fact = knowledge.amenityFacts.find((item) => /\bparking|cars?|vehicles?|garage\b/i.test(item));
    return fact
      ? { answer: `Yes — ${p.name} has ${lowerFirst(fact)}.`, refs: [p.name] }
      : handoffAnswer(p, text);
  }

  if (/\bgolf\b/i.test(text) && !/\bgolf\s*[- ]?cart\b/i.test(text)) {
    return handoffAnswer(p, text);
  }

  if (/\b(?:bars?|restaurants?|dining|shopping|nightlife|nearby|close to|walk to|around|food|eat|drink)\b/i.test(text)) {
    const intent = /\bbar|restaurant|dining|shopping|nightlife|food|eat|drink|live music|entertainment|walkable\b/i;
    const placeSummaries = glossarySummary(knowledge.nearbyPlaces, intent).slice(0, 3);
    const facts = knowledge.locationFacts.slice(0, 4);
    const answer = [
      `Yes — ${p.name} has several nearby options listed in the property details.`,
      sentenceList(facts),
      placeSummaries.length ? placeSummaries.join(' ') : '',
    ]
      .filter(Boolean)
      .join(' ');
    return {
      answer,
      refs: [p.name],
    };
  }

  if (/\broom\s+layout|bed(?:room)?\s+layout|sleeping\s+layout|which bedrooms?|beds?|king|queen|bunk|sleeper sofa|sofa sleeper\b/i.test(text)) {
    return {
      answer: `${p.name} sleeping layout: ${sentenceList(knowledge.roomLayoutFacts)}`,
      refs: [p.name],
    };
  }

  if (/\bamenities|included|features|what comes with|what does it have\b/i.test(text)) {
    return {
      answer: `${p.name} includes: ${sentenceList(knowledge.amenityFacts.slice(0, 8))}`,
      refs: [p.name],
    };
  }

  if (/\bfacts?|details|summary|overview\b/i.test(text)) {
    return {
      answer: `${p.name}: ${knowledge.propertyFacts.join(', ')}. Key details: ${sentenceList(knowledge.amenityFacts.slice(0, 4))}`,
      refs: [p.name],
    };
  }

  return null;
}

function commonPropertyQuestionAnswer(p: VerifiedProperty, text: string): ConciergeExchange | null {
  const knowledgeAnswer = propertyKnowledgeAnswer(p, text);
  if (knowledgeAnswer) return knowledgeAnswer;

  if (/\b(?:bars?|restaurants?|dining|shopping|nightlife|nearby|close to|walk to)\b/i.test(text)) {
    return null;
  }

  if (/\bgolf\b/i.test(text) && !/\bgolf\s*[- ]?cart\b/i.test(text)) {
    return handoffAnswer(p, text);
  }

  if (/\blinens?|sheets?|towels?|bedding|bath towels?\b/i.test(text)) {
    const linens: Partial<Record<VerifiedProperty['id'], string>> = {
      belvedere:
        'Yes — The Belvedere page specifically notes that beach towels are provided. For full linen details, the Inspire team can confirm exactly what is included before arrival.',
      'sea-dreams':
        'The Sea Dreams reviews mention bath towels, stocked bathrooms, and plenty of towels, along with shampoo and conditioner. For full linen details, the Inspire team can confirm exactly what is included before arrival.',
      'hannahs-pearl':
        'Hannah’s Pearl guest reviews call out the beds and bedding as a highlight. For full linen and towel details, the Inspire team can confirm exactly what is included before arrival.',
    };
    return linens[p.id] ? { answer: linens[p.id], refs: [p.name] } : handoffAnswer(p, text);
  }

  if (/\bparking|cars?|vehicles?|garage\b/i.test(text)) {
    const parking: Partial<Record<VerifiedProperty['id'], string>> = {
      belvedere: 'Yes — The Belvedere has private parking for two vehicles, including a private garage.',
      'sea-dreams': 'Yes — Sea Dreams has parking for up to 3 vehicles, depending on vehicle size and type.',
      'beach-beach-baby': 'Yes — Beach Beach Baby has parking for 6 to 8 vehicles, depending on vehicle size and type.',
      'bella-vita': 'Yes — Bella Vita includes access to a covered parking garage with a connected sky bridge.',
      'sunset-bungalow-30a': 'Yes — Sunset Bungalow on 30A has on-site parking for up to 4 vehicles.',
    };
    return parking[p.id] ? { answer: parking[p.id], refs: [p.name] } : handoffAnswer(p, text);
  }

  if (/\bshowers?|tubs?|bathtub|bathrooms?\b/i.test(text)) {
    const baths: Partial<Record<VerifiedProperty['id'], string>> = {
      'sea-dreams':
        'Sea Dreams has 5 bathrooms. The primary bathroom includes a jetted tub and walk-in shower, and each bedroom has its own bathroom.',
      'hannahs-pearl':
        'Hannah’s Pearl has 5.5 bathrooms. The primary suite includes a soaking tub, glass-front walk-in shower, and dual-sink granite vanity. One guest bedroom has an en suite shower/tub, and the carriage house has a walk-in shower.',
      'sunset-bungalow-30a':
        'Sunset Bungalow on 30A has 2 bathrooms. The primary bathroom has a walk-in shower and double vanity, and the guest bedrooms share a second full bathroom with a shower/tub combination.',
      'bella-vita':
        'Bella Vita has 2 bathrooms. The primary bathroom includes double sinks, a garden tub, and a separate walk-in shower.',
    };
    return baths[p.id] ? { answer: baths[p.id], refs: [p.name] } : handoffAnswer(p, text);
  }

  return null;
}

function asksForFamiliesWithKids(text: string): boolean {
  return /\bfamil(?:y|ies)\b|\bkids?\b|\bchildren\b|\bchild\b|\btoddlers?\b|\bteenagers?\b|\bteens?\b|\bbunk\b|\bplay\b|\bgames?\b/i.test(text);
}

function asksForMultiFamily(text: string): boolean {
  return /\bmulti[- ]?family\b|\btwo families\b|\bmultiple families\b|\bfamilies traveling together\b/i.test(text);
}

function asksForLargeGroup(text: string): boolean {
  return /\blarge group\b|\bbig group\b|\broom for a crowd\b|\beveryone\b|\bwhole family\b|\bstrong fit for a large group\b/i.test(text);
}

function asksForPoolAccess(text: string): boolean {
  return /\bpool\b|\bsplash\s*pad\b|\bhot\s*tub\b/i.test(text);
}

function asksForWalkableTown(text: string): boolean {
  return /\bwalk(?:able|ing)?\b|\bwalk\s*to\b|\btown\b|\bshopping\b|\bdining\b|\brestaurants?\b|\bbars?\b|\bnightlife\b/i.test(text);
}

function asksForQuiet30A(text: string): boolean {
  return /\bquiet\b|\bcalm\b|\brelaxed\b|\bslower\b|\b30a rhythm\b/i.test(text);
}

function mostAnswer(pool: VerifiedProperty[]): ConciergeExchange {
  if (pool.length === 0) {
    return {
      answer: "The best place to start is with the larger homes in the collection, then compare the property pages for the details that matter most to your group.",
    };
  }
  const max = Math.max(...pool.map((p) => p.sleeps));
  const top = pool.filter((p) => p.sleeps === max);
  if (top.length === 1) {
    const p = top[0];
    return {
      answer: `${p.name} sleeps the most \u2014 up to ${p.sleeps} guests. It's in ${p.location}.`,
      refs: [p.name],
    };
  }
  return {
    answer: `${joinNatural(top.map((p) => p.name))} sleep the most \u2014 up to ${max} guests each.`,
    refs: top.map((p) => p.name),
  };
}

function petAnswer(named: VerifiedProperty[]): ConciergeExchange {
  if (named.length) {
    const p = named[0];
    if (p.petPolicy) {
      return {
        answer: `Yes — ${p.name} is listed as ${p.petPolicy.toLowerCase()} The property page is the best place to review the latest pet details before you book.`,
        refs: [p.name],
      };
    }
    const nearbyPetFriendly = verifiedProperties
      .filter((candidate) => candidate.petPolicy && candidate.id !== p.id)
      .filter((candidate) => candidate.area === p.area || candidate.communities.some((community) => p.communities.includes(community)))
      .slice(0, 2);
    if (nearbyPetFriendly.length > 0) {
      return {
        answer: `I don’t see ${p.name} listed as pet friendly. For a similar area, I’d look at ${joinNatural(
          nearbyPetFriendly.map((candidate) => `${candidate.name} in ${candidate.location}`),
        )}; ${nearbyPetFriendly.length > 1 ? 'they are' : 'it is'} listed as pet friendly, with current fees and approval details on the property page.`,
        refs: [p.name, ...nearbyPetFriendly.map((candidate) => candidate.name)],
      };
    }
    return {
      answer: `I don’t see ${p.name} listed as pet friendly. The Inspire team can confirm whether there are any exceptions before you book.`,
      refs: [p.name],
    };
  }
  const petFriendly = verifiedProperties.filter((p) => p.petPolicy);
  if (petFriendly.length) {
    return {
      answer: `${joinNatural(petFriendly.map((p) => p.name))} ${petFriendly.length > 1 ? 'are listed' : 'is listed'} as pet friendly. The property pages will walk you through current pet fees and approval details before you book.`,
      refs: petFriendly.map((p) => p.name),
    };
  }
  return {
    answer:
      'For pet-friendly stays, start with the pet-friendly collection and review the property page for current approval details before you book.',
  };
}

function compareAnswer(named: VerifiedProperty[]): ConciergeExchange {
  const [a, b] = named;
  return {
    answer: `${a.name} is in ${a.location} \u2014 ${a.summarySentence}. ${b.name} is in ${b.location} \u2014 ${b.summarySentence}. In short: ${a.tag} versus ${b.tag}.`,
    refs: [a.name, b.name],
  };
}

function amenityAnswer(
  def: AmenityDef,
  fits: VerifiedProperty[],
  region: string | null,
  scoped = false,
): ConciergeExchange {
  const setLabel = scoped ? "the properties you've saved" : 'our properties';
  if (fits.length === 0) {
    return {
      answer: `For ${region ?? 'this search'}, the best next step is to compare the property pages for ${def.phrase} and choose the stay that fits your group best.`,
    };
  }
  if (fits.length === 1) {
    const p = fits[0];
    const others = amenityPhrases(p).filter((ph) => ph !== def.phrase && !ph.includes(def.phrase)).slice(0, 2);
    const tail = others.length
      ? ` It's located in ${p.location} and also includes ${joinNatural(others)}.`
      : ` It's located in ${p.location}.`;
    const lead = def.key === 'golfCart'
      ? `${p.name} is the one with a golf cart.`
      : `${p.name} ${def.verb} ${def.phrase}.`;

    return {
      answer: `${lead}${tail}`,
      refs: [p.name],
    };
  }
  const list = fits.map((p) => `${p.name} in ${p.location}`);
  if (fits.length === 2) {
    return {
      answer: `${list[0]} and ${list[1]} both ${def.verb === 'is' ? 'are' : 'have'} ${def.phrase}.`,
      refs: fits.map((p) => p.name),
    };
  }
  return {
    answer: `${joinNatural(list)} ${def.verb === 'is' ? 'are' : 'all have'} ${def.phrase}.`,
    refs: fits.map((p) => p.name),
  };
}

function guestAnswer(pool: VerifiedProperty[], n: number, scoped = false): ConciergeExchange {
  const setLabel = scoped ? "the properties you've saved" : 'our properties';
  const fit = pool.filter((p) => p.sleeps >= n);
  const notFit = pool.filter((p) => p.sleeps < n);

  let answer: string;
  if (fit.length === 0) {
    answer = `${setLabel === 'our properties' ? 'Beach Beach Baby' : 'The largest saved option'} is where I would start for a bigger group. Beach Beach Baby sleeps up to 28, and the property pages make it easy to compare layouts before you choose.`;
    return { answer };
  }

  const ranked = fit
    .map((p) => {
      const extraCapacity = p.sleeps - n;
      const capacityScore = extraCapacity <= 4 ? 0 : extraCapacity <= 10 ? 1 : 2;
      const amenityScore = (p.privatePool ? 1 : 0) + (p.beachfront ? 1 : 0) + (p.golfCart ? 1 : 0);
      return { property: p, capacityScore, amenityScore };
    })
    .sort((a, b) => a.capacityScore - b.capacityScore || b.amenityScore - a.amenityScore || a.property.sleeps - b.property.sleeps);

  const best = ranked[0]?.property;
  const also = ranked.slice(1).filter((entry) => entry.capacityScore <= 1).slice(0, 2).map((entry) => entry.property);
  const oversized = ranked.filter((entry) => entry.capacityScore > 1).map((entry) => entry.property);
  const refs = [best, ...also].filter((p): p is VerifiedProperty => Boolean(p)).map((p) => p.name);

  if (!best) {
    return {
      answer: `${joinNatural(fit.map((p) => p.name))} fit ${n} guests. From there, I would narrow by area, pool, beach access, and the kind of trip you want.`,
      refs: fit.map((p) => p.name),
    };
  }

  const bestDetails = `${best.name} is where I would start for ${n} guests. It sleeps up to ${best.sleeps}, has ${best.bedrooms} bedrooms, and ${best.summarySentence}.`;
  const alsoDetails = also.length
    ? `\n\nAlso worth comparing: ${joinNatural(
        also.map((p) => `${p.name} (${p.location}, sleeps up to ${p.sleeps})`),
      )}.`
    : '';
  const oversizedDetails = oversized.length
    ? `\n\n${joinNatural(oversized.map((p) => p.name))} also ${oversized.length > 1 ? 'fit' : 'fits'}, but ${oversized.length > 1 ? 'they are' : 'it is'} much larger than you may need.`
    : '';
  const tightDetails = notFit.length
    ? ` ${joinNatural(notFit.map((p) => p.name))} sleeps up to ${Math.max(...notFit.map((p) => p.sleeps))}, so I would only keep it in play for a smaller group.`
    : '';

  answer = `${bestDetails}${alsoDetails}${oversizedDetails}${tightDetails}`;
  return { answer, refs };
}

function regionAnswer(pool: VerifiedProperty[], region: string, scoped = false): ConciergeExchange {
  const prefix = scoped ? 'Your saved properties in' : 'Our properties in';
  const list = pool.map((p) => `${p.name} in ${p.location}`);
  const sleeps = pool.map((p) => `${p.name} sleeps up to ${p.sleeps}`);
  return {
    answer: `${prefix} ${region}: ${joinNatural(list)}. ${sleeps.join('. ')}.`,
    refs: pool.map((p) => p.name),
  };
}

function poolPhrase(p: VerifiedProperty): string {
  if (p.privatePool && p.id === 'sea-dreams') return 'a Gulf-front private pool';
  if (p.privatePool) return 'a private pool';
  if (p.id === 'bella-vita') return 'resort pools, a hot tub, and splash pad';
  return 'pool access';
}

function hasPoolAccess(p: VerifiedProperty): boolean {
  return p.privatePool || p.verifiedAmenityPhrases.some((phrase) => /\bpools?\b|\bsplash\s*pad\b|\bhot\s*tub\b/i.test(phrase));
}

function largeGroupAnswer(pool: VerifiedProperty[]): ConciergeExchange {
  const ranked = pool.slice().sort((a, b) => b.sleeps - a.sleeps).slice(0, 3);
  if (ranked.length === 0) {
    return {
      answer:
        "For a large group, I'd recommend our most spacious property. The Inspire team can walk you through the options.",
    };
  }
  return {
    answer: ranked
      .map((p, index) =>
        index === 0
          ? `${p.name} is the strongest large-group fit, sleeping up to ${p.sleeps} guests with ${p.bedrooms} bedrooms.`
          : `${p.name} is also worth comparing for a large group, with room for up to ${p.sleeps} guests.`,
      )
      .join('\n\n'),
    refs: ranked.map((p) => p.name),
  };
}

function multiFamilyAnswer(pool: VerifiedProperty[], guestCount: number | null): ConciergeExchange {
  const candidates = pool
    .filter((p) => !guestCount || p.sleeps >= guestCount)
    .map((p) => {
      const score =
        (p.carriageHouse ? 3 : 0) +
        (p.bedrooms >= 5 ? 2 : 0) +
        (p.privatePool ? 1 : 0) +
        (p.bathrooms.includes('.') || Number(p.bathrooms) >= 5 ? 1 : 0);
      return { property: p, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.property.sleeps - a.property.sleeps)
    .slice(0, 3)
    .map((entry) => entry.property);

  if (candidates.length === 0) return guestCount ? guestAnswer(pool, guestCount) : largeGroupAnswer(pool);

  return {
    answer: candidates
      .map((p, index) =>
        index === 0
          ? `${p.name} is where I would start for a multi-family trip: it sleeps up to ${p.sleeps}, has ${p.bedrooms} bedrooms, and ${p.summarySentence}.`
          : `${p.name} is also worth comparing for multiple families, especially if you want ${p.privatePool ? poolPhrase(p) : 'more room to spread out'}.`,
      )
      .join('\n\n'),
    refs: candidates.map((p) => p.name),
  };
}

function familyAnswer(pool: VerifiedProperty[]): ConciergeExchange {
  const byId = new Map(pool.map((p) => [p.id, p]));
  const hannahsPearl = byId.get('hannahs-pearl');
  const seaDreams = byId.get('sea-dreams');
  const beachBeachBaby = byId.get('beach-beach-baby');
  const bellaVita = byId.get('bella-vita');
  const sunsetBungalow = byId.get('sunset-bungalow-30a');

  const recommendations: string[] = [];
  const refs: string[] = [];

  if (hannahsPearl) {
    recommendations.push(
      `${hannahsPearl.name} is where I would start for a family trip: private pool, carriage house, golf cart, bikes, and WaterSound West beach access.`,
    );
    refs.push(hannahsPearl.name);
  }

  if (seaDreams) {
    recommendations.push(
      `${seaDreams.name} is the beachfront family pick, with a Gulf-front private pool, Xbox, board games, pack-and-play, and high chair.`,
    );
    refs.push(seaDreams.name);
  }

  if (beachBeachBaby) {
    recommendations.push(
      `${beachBeachBaby.name} is best when the family group is huge, with bunk-heavy sleeping, a private pool, hot tub, elevator, and beach access out front.`,
    );
    refs.push(beachBeachBaby.name);
  }

  if (bellaVita && recommendations.length < 3) {
    recommendations.push(
      `${bellaVita.name} works for a smaller family that wants a Gulf-front condo with bunks, resort pools, a hot tub, and splash pad.`,
    );
    refs.push(bellaVita.name);
  }

  if (sunsetBungalow && recommendations.length < 3) {
    recommendations.push(
      `${sunsetBungalow.name} is a relaxed 30A cottage option with a bunk room, bikes, Gulf views, and beach access steps away.`,
    );
    refs.push(sunsetBungalow.name);
  }

  if (recommendations.length === 0) {
    return {
      answer:
        'For families, I would look for the best mix of sleeping layout, beach access, pool time, and easy ways for kids to stay entertained.',
    };
  }

  return {
    answer: `${recommendations.join('\n\n')}\n\nIf you want the easiest family short list, compare ${joinNatural(refs.slice(0, 3))}.`,
    refs: refs.slice(0, 3),
  };
}

function poolAnswer(pool: VerifiedProperty[], guestCount: number | null): ConciergeExchange {
  const candidates = pool
    .filter((p) => hasPoolAccess(p))
    .filter((p) => !guestCount || p.sleeps >= guestCount)
    .sort((a, b) => Number(b.privatePool) - Number(a.privatePool) || a.sleeps - b.sleeps)
    .slice(0, 3);

  if (candidates.length === 0) {
    return guestCount
      ? {
          answer: `For ${guestCount} guests, I would start with the properties that fit your group size first, then compare pool options on the property pages.`,
          refs: pool.filter((p) => p.sleeps >= guestCount).map((p) => p.name),
        }
      : {
          answer: 'For pool time, I would focus on the properties with private pools or resort pool access.',
        };
  }

  return {
    answer: candidates
      .map((p, index) =>
        index === 0
          ? `${p.name} is where I would start for pool time: ${poolPhrase(p)}, ${p.location}, and room for up to ${p.sleeps} guests.`
          : `${p.name} is also worth comparing, with ${poolPhrase(p)} and room for up to ${p.sleeps} guests.`,
      )
      .join('\n\n'),
    refs: candidates.map((p) => p.name),
  };
}

function quiet30AAnswer(pool: VerifiedProperty[], guestCount: number | null): ConciergeExchange {
  const candidates = pool
    .filter((p) => p.communities.includes('30A'))
    .filter((p) => !guestCount || p.sleeps >= guestCount)
    .sort((a, b) => {
      const score = (p: VerifiedProperty) =>
        (p.id === 'hannahs-pearl' ? 3 : 0) +
        (p.id === 'sunset-bungalow-30a' ? 2 : 0) +
        (p.communities.includes('WaterSound') ? 1 : 0) +
        (p.communities.includes('Dune Allen') ? 1 : 0);
      return score(b) - score(a) || a.sleeps - b.sleeps;
    })
    .slice(0, 3);

  if (candidates.length === 0) return regionAnswer(pool, '30A');

  return {
    answer: candidates
      .map((p, index) =>
        index === 0
          ? `${p.name} is where I would start for a quieter 30A stay: ${p.location}, room for up to ${p.sleeps} guests, and ${p.summarySentence}.`
          : `${p.name} is also worth comparing for a relaxed 30A rhythm, with room for up to ${p.sleeps} guests.`,
      )
      .join('\n\n'),
    refs: candidates.map((p) => p.name),
  };
}

function walkableAnswer(pool: VerifiedProperty[]): ConciergeExchange {
  const byId = new Map(pool.map((p) => [p.id, p]));
  const belvedere = byId.get('belvedere');
  const seaDreams = byId.get('sea-dreams');
  const sunsetBungalow = byId.get('sunset-bungalow-30a');
  const refs: string[] = [];
  const parts: string[] = [];

  if (belvedere) {
    parts.push(
      `${belvedere.name} is the strongest walkable-to-town choice, with Rosemary Beach restaurants, boutiques, and the beach just steps from the front door.`,
    );
    refs.push(belvedere.name);
  }

  if (seaDreams) {
    parts.push(
      `${seaDreams.name} is also a good fit in Panama City Beach, with restaurants, bars, and shopping listed within walking distance.`,
    );
    refs.push(seaDreams.name);
  }

  if (sunsetBungalow) {
    parts.push(
      `${sunsetBungalow.name} is more of a relaxed 30A cottage pick, with Stinky's Fish Camp across the street and other Dune Allen favorites nearby.`,
    );
    refs.push(sunsetBungalow.name);
  }

  if (parts.length === 0) {
    return {
      answer:
        'For walkable dining and shopping, I would focus on properties with restaurants, shops, or town-center access close by.',
    };
  }

  return {
    answer: parts.join('\n\n'),
    refs: refs.slice(0, 3),
  };
}

function constraintAnswer(opts: {
  text: string;
  guestCount: number | null;
  region: ReturnType<typeof detectRegion>;
  amenities: AmenityKey[];
  named: VerifiedProperty[];
  pool?: VerifiedProperty[];
  scoped?: boolean;
}): ConciergeExchange {
  let pool = (opts.pool ?? verifiedProperties).slice();
  if (opts.named.length) pool = pool.filter((p) => opts.named.some((n) => n.id === p.id));
  if (opts.region) pool = pool.filter((p) => inRegion(p, opts.region));
  const scoped = opts.scoped ?? false;
  const detailFits = pool
    .map((p) => ({ property: p, details: matchingDetailPhrases(p, opts.text) }))
    .filter((entry) => entry.details.length > 0);

  const amenityDef = opts.amenities.length
    ? AMENITIES.find((a) => a.key === opts.amenities[0])
    : undefined;

  if (asksForMultiFamily(opts.text)) {
    return multiFamilyAnswer(pool, opts.guestCount);
  }

  if (asksForLargeGroup(opts.text)) {
    return largeGroupAnswer(pool);
  }

  if (asksForPoolAccess(opts.text) && !/\bprivate pool\b/i.test(opts.text)) {
    return poolAnswer(pool, opts.guestCount);
  }

  if (opts.region === '30A' && asksForQuiet30A(opts.text)) {
    return quiet30AAnswer(pool, opts.guestCount);
  }

  if (opts.named.length === 1) {
    const commonAnswer = commonPropertyQuestionAnswer(opts.named[0], opts.text);
    if (commonAnswer) {
      return commonAnswer;
    }
  }

  if (asksForFamiliesWithKids(opts.text)) {
    return familyAnswer(pool);
  }

  if (asksForWalkableTown(opts.text)) {
    return walkableAnswer(pool);
  }

  if (opts.guestCount && amenityDef) {
    const both = pool.filter((p) => p.sleeps >= opts.guestCount! && p[amenityDef.key]);
    if (both.length) {
      const list = both.map((p) => `${p.name} in ${p.location} sleeps up to ${p.sleeps}`);
      return {
        answer: `${joinNatural(both.map((p) => p.name))} ${both.length > 1 ? 'fit' : 'fits'} ${opts.guestCount} guests and ${
          both.length > 1 ? 'offer' : 'offers'
        } ${amenityDef.phrase}. ${joinNatural(list)}.`,
        refs: both.map((p) => p.name),
      };
    }

    const hasAmenity = pool.filter((p) => p[amenityDef.key]);
    const hasSleeps = pool.filter((p) => p.sleeps >= opts.guestCount!);
    let answer = '';
    if (hasAmenity.length) {
      answer += `${joinNatural(hasAmenity.map((p) => p.name))} ${hasAmenity.length > 1 ? 'have' : 'has'} ${amenityDef.phrase}, but ${
        hasAmenity.length > 1 ? 'they sleep' : 'it sleeps'
      } up to ${Math.max(...hasAmenity.map((p) => p.sleeps))}. `;
    }
    if (hasSleeps.length) {
      answer += `${hasSleeps.length > 1 ? 'The properties that fit' : 'The property that fits'} ${opts.guestCount} guests ${
        hasSleeps.length > 1 ? 'are' : 'is'
      } ${joinNatural(hasSleeps.map((p) => `${p.name} (up to ${p.sleeps})`))}, and ${
        hasSleeps.length > 1 ? 'none of them' : 'it'
      } ${hasSleeps.length > 1 ? 'list' : 'lists'} ${amenityDef.phrase}. `;
    }
    answer += 'The property pages are the best place to compare the final details before you choose.';
    return {
      answer,
      refs: [...hasAmenity.map((p) => p.name), ...hasSleeps.map((p) => p.name)],
    };
  }

  if (amenityDef) {
    return amenityAnswer(amenityDef, pool.filter((p) => p[amenityDef.key]), opts.region, scoped);
  }

  if (detailFits.length > 0) {
    const refs = detailFits.map((entry) => entry.property.name);
    const answer = detailFits
      .map((entry) => `Yes — ${entry.property.name} has ${joinNatural(entry.details.slice(0, 3))}`)
      .join('. ');
    return {
      answer: `${answer}.`,
      refs,
    };
  }

  if (asksForVerifiedDetail(opts.text) && opts.named.length > 0) {
    return {
      answer: `${joinNatural(opts.named.map((p) => p.name))} is a great one to look at. The property page is the best place to check the latest details before you book.`,
      refs: opts.named.map((p) => p.name),
    };
  }

  if (asksForVerifiedDetail(opts.text)) {
    return {
      answer:
        "The best next step is to open the property page that catches your eye and check the latest details there before you book.",
    };
  }

  if (opts.guestCount) {
    return guestAnswer(pool, opts.guestCount, scoped);
  }

  if (opts.region) {
    return regionAnswer(pool, opts.region, scoped);
  }

  if (opts.named.length) {
    const p = opts.named[0];
    return {
      answer: `${p.name} is in ${p.location}. It ${p.summarySentence}.`,
      refs: [p.name],
    };
  }

  return {
    answer:
      "That's a good question. I can tell you about our properties and what each one offers \u2014 beachfront, private pools, room for a crowd, or a golf cart to get around. Ask me about any of those, and I'll point you in the right direction.",
  };
}

/** Core logic shared by both homepage and property-page concierge */
function coreAnswer(input: string, savedIds?: string[]): ConciergeExchange {
  const text = input.trim();
  const named = detectNamedProperties(text);

  if (/\bdog\s+run|fenced\s+(?:yard|area)|dog\s+area\b/i.test(text) && named.length === 1) {
    return handoffAnswer(named[0], text);
  }

  const placeMatches = detectPlaces(text);
  if (placeMatches.length > 0 && /\bwhat(?:'s| is)|tell me about|explain|where is|who is\b/i.test(text)) {
    return {
      answer: placeMatches.map((key) => PLACE_GLOSSARY[key]?.summary).filter(Boolean).join(' '),
    };
  }

  if (/\bavailab|book|reserve|dates?|calendar|check[- ]?in|pricing|rates?\b/i.test(text)) {
    const namedForDates = detectNamedProperties(text);
    if (namedForDates.length > 0) {
      const p = namedForDates[0];
      return {
        answer: `${p.name} is a wonderful choice. Open the property page, enter your dates in the Book This Property section, and you'll be able to review current availability, pricing, and booking details there.`,
        refs: [p.name],
      };
    }

    return {
      answer:
        "Absolutely. Choose the property you like, then enter your dates in the Book This Property section to review current availability, pricing, and booking details.",
    };
  }

  if (/\bpet|pets|dog|dogs|cat|cats|animals?\b/i.test(text)) {
    return petAnswer(named);
  }

  if (named.length >= 2 && /\bdiffer|compare|between|versus|\bvs\b/i.test(text)) {
    return compareAnswer(named);
  }

  const guestCount = extractGuestCount(text);
  const region = detectRegion(text);
  const amenities = detectAmenities(text);

  const savedList = savedIds ?? [];
  const useSaved = savedList.length >= 1 && referencesSavedSet(text);

  let pool = verifiedProperties.slice();
  if (useSaved) {
    pool = pool.filter((p) => savedList.includes(p.id));
  }

  const wantsMost = /\bmost\b|\blargest\b|\bbiggest\b/i.test(text);
  if (wantsMost && !guestCount) {
    return mostAnswer(pool);
  }

  if (asksForMultiFamily(text) || asksForLargeGroup(text) || asksForPoolAccess(text) || asksForFamiliesWithKids(text) || asksForQuiet30A(text)) {
    return constraintAnswer({ text, guestCount, region, amenities, named, pool, scoped: useSaved });
  }

  if (asksForWalkableTown(text)) {
    return constraintAnswer({ text, guestCount, region, amenities, named, pool, scoped: useSaved });
  }

  if (guestCount || amenities.length || region || named.length || useSaved || asksForVerifiedDetail(text)) {
    return constraintAnswer({ text, guestCount, region, amenities, named, pool, scoped: useSaved });
  }

  if (/\blarge|big|multi[- ]?family|group|everyone|whole family\b/i.test(text)) {
    return largeGroupAnswer(pool);
  }

  return {
    answer:
      "That's a good question. I can tell you about our properties and what each one offers \u2014 beachfront, private pools, room for a crowd, or a golf cart to get around. Ask me about any of those, and I'll point you in the right direction.",
  };
}

/**
 * Main entry point.
 * Pass contextPropertyId (e.g. 'belvedere') when the guest is on a specific property page.
 * The engine will first try to answer from that property's verified facts, then fall back to
 * the full collection for comparison/cross-property questions.
 */
export function answerConcierge(input: string, contextPropertyId?: string, savedIds?: string[]): ConciergeExchange {
  // Questions about the guest's saved/compare selections resolve against that set
  if (savedIds && savedIds.length >= 1 && referencesSavedSet(input.trim())) {
    return coreAnswer(input, savedIds);
  }

  if (contextPropertyId) {
    const ctx = verifiedProperties.find((p) => p.id === contextPropertyId);
    if (ctx) {
      const text = input.trim();
      const namedOnPropertyPage = detectNamedProperties(text);
      const isCrossCollectionQuestion =
        namedOnPropertyPage.length >= 2 ||
        /\bother (?:homes?|properties)|compare|all (?:homes?|properties)|which (?:homes?|properties)|how does it compare|\bmost\b|\blargest\b|\bbiggest\b/i.test(text);
      const target = !isCrossCollectionQuestion && namedOnPropertyPage.length === 1 ? namedOnPropertyPage[0] : ctx;

      if (/\bdog\s+run|fenced\s+(?:yard|area)|dog\s+area\b/i.test(text)) {
        return handoffAnswer(target, text);
      }

      // Availability / booking
      if (/\bavailab|book|reserve|dates?|calendar|check[- ]?in|pricing|rates?\b/i.test(text)) {
        return {
          answer: `${target.name} is a wonderful choice. Use the Book This Property section on the property page to enter your dates and review current availability, pricing, and booking details.`,
          refs: [target.name],
        };
      }

      // Pet policy
      if (/\bpet|pets|dog|dogs|cat|cats|animals?\b/i.test(text)) {
        if (target.petPolicy) {
          return {
            answer: `Yes — ${target.name} is listed as ${target.petPolicy.toLowerCase()} The property page is the best place to review the latest pet details before you book.`,
            refs: [target.name],
          };
        }
        return petAnswer([target]);
      }

      // Guest count
      const guestCount = extractGuestCount(text);
      if (guestCount) {
        if (guestCount <= target.sleeps) {
          return {
            answer: `Yes \u2014 ${target.name} sleeps up to ${target.sleeps}, so it's a great fit for ${guestCount}. It ${target.summarySentence}.`,
            refs: [target.name],
          };
        } else {
          return {
            answer: `${target.name} sleeps up to ${target.sleeps} guests, so it would be a bit snug for ${guestCount}. The Inspire team can help you find the right fit.`,
            refs: [target.name],
          };
        }
      }

      if (asksForFamiliesWithKids(text)) {
        if (/which|best|recommend|compare|other|all/i.test(text)) {
          return coreAnswer(input, savedIds);
        }
        return {
          answer: `${target.name} can be a strong family fit. It ${target.summarySentence}.`,
          refs: [target.name],
        };
      }

      const commonAnswer = commonPropertyQuestionAnswer(target, text);
      if (commonAnswer) {
        return commonAnswer;
      }

      // Specific amenity question
      const amenities = detectAmenities(text);
      const detailMatches = matchingDetailPhrases(target, text);
      if (detailMatches.length > 0 && amenities.length === 0) {
        return {
          answer: `Yes — ${target.name} has ${joinNatural(detailMatches)}.`,
          refs: [target.name],
        };
      }
      if (amenities.length > 0) {
        const has: string[] = [];
        const lacks: string[] = [];
        for (const key of amenities) {
          const def = AMENITIES.find((a) => a.key === key);
          if (!def) continue;
          if (target[key]) has.push(def.phrase);
          else lacks.push(def.phrase);
        }
        if (has.length > 0 && lacks.length === 0) {
          return {
            answer: `Yes \u2014 ${target.name} has ${joinNatural(has)}. It ${target.summarySentence}.`,
            refs: [target.name],
          };
        } else if (has.length === 0) {
          return {
            answer: amenityPivotAnswer(target, lacks),
            refs: [target.name],
          };
        } else {
          return {
            answer: `Yes \u2014 ${target.name} has ${joinNatural(has)}. ${amenityPivotAnswer(target, lacks)}`,
            refs: [target.name],
          };
        }
      }

      const detailAnswer = propertyDetailAnswer(target, text);
      if (detailAnswer) {
        return {
          answer: detailAnswer,
          refs: [target.name],
        };
      }

      // General overview
      if (/\btell me|about this|this home|this house|this property|overview|describe/i.test(text)) {
        const phrases = amenityPhrases(target);
        return {
          answer: `${target.name} is in ${target.location} \u2014 it ${target.summarySentence}. With ${target.bedrooms} bedrooms and ${target.bathrooms} bathrooms, it sleeps up to ${target.sleeps} guests.${phrases.length ? ` It offers ${joinNatural(phrases)}.` : ''}`,
          refs: [target.name],
        };
      }

      // Cross-collection questions fall through to coreAnswer
      if (!isCrossCollectionQuestion) {
        return handoffAnswer(target, text);
      }
    }
  }

  return coreAnswer(input, savedIds);
}
